import { EventType, Preset, Visibility } from "matrix-js-sdk";
import { matrixClient } from "./client";
import { Log } from "@fanfanlo";

/**
 * 邀请/创建房间操作的返回结构
 */
export interface DirectMessageResult {
    success: boolean;
    roomId?: string;
    error?: string;
    details?: string;
}

const fileLogger = new Log(false, 'im.client.invite');


// 验证用户ID格式
function isValidUserId(userId: string): boolean {
    if (!userId) return false;
    // 简单的用户ID格式验证，根据实际需求调整
    return /^@[^:]+:.+$/.test(userId);
}

// 检查是否是自己
function isSelfUserId(userId: string): boolean {
    return matrixClient.client?.getUserId() === userId;
}

// 检查是否已经存在与目标用户的直接消息房间
async function findExistingDM(userId: string): Promise<string | null> {
    const logger = fileLogger.sub(false, 'findExistingDM');
    // logger.pause = true;
    logger.log(`Checking existing DM for user: ${userId}`);

    if (!matrixClient.client) {
        logger.error("Matrix client is not available");
        return null;
    }

    // 获取房间列表
    const rooms = await matrixClient.client.getRooms();
    logger.log(`Got rooms: ${rooms ? rooms.length : 'null'}`);

    if (!rooms) {
        logger.error("No rooms available");
        return null;
    }

    // 确保 rooms 是可迭代的
    const roomList = Array.isArray(rooms) ? rooms : [rooms];
    logger.log(`Room list length: ${roomList.length}`);

    for (const room of roomList) {
        if (!room) {
            logger.warn("Found null room, skipping");
            continue;
        }

        logger.log(`Checking room: ${room.roomId} (${room.name})`);

        // 检查当前用户状态
        const member = room.getMember(matrixClient.client?.getUserId() || '');
        logger.log(`Current user member info: ${JSON.stringify(member)}`);
        
        if (!member) {
            logger.warn(`No member info found for current user in room ${room.roomId}`);
            continue;
        }

        logger.log(`Current user membership: ${member.membership}`);

        if (member.membership !== 'join') {
            logger.log(`Skipping room ${room.roomId} because current user is not joined`);
            continue;
        }

        // 检查是否是直接消息房间
        const isDirect = matrixClient.client.getAccountData(EventType.Direct)?.getContent()?.[userId]?.includes(room.roomId);
        logger.log(`Room ${room.roomId} isDirect: ${isDirect}`);

        if (isDirect) {
            // 确认目标用户在房间中
            const targetMember = room.getMember(userId);
            logger.log(`Target user member info: ${JSON.stringify(targetMember)}`);
            
            if (targetMember?.membership === 'join') {
                logger.log(`Found existing DM room: ${room.roomId}`);
                return room.roomId;
            }
        }
    }

    logger.log("No existing DM room found");
    return null;
}
// 创建直接消息房间
async function createDirectMessage(userId: string, maxRetries = 2): Promise<DirectMessageResult> {
    const logger = fileLogger.sub(true, 'createDirectMessage');
    
    // 输入验证
    if (!matrixClient.client) {
        return { success: false, error: 'Matrix 客户端未初始化' };
    }
    
    if (!userId) {
        return { success: false, error: '用户ID不能为空' };
    }
    
    if (!isValidUserId(userId)) {
        return { success: false, error: '无效的用户ID格式' };
    }
    
    // 检查是否是自己
    if (isSelfUserId(userId)) {
        return { success: false, error: '不能给自己发送直接消息' };
    }
    
    // 检查是否已存在直接消息房间
    const existingRoomId = await findExistingDM(userId);
    if (existingRoomId) {
        logger.log(`已存在与用户 ${userId} 的直接消息房间: ${existingRoomId}`);
        return { success: true, roomId: existingRoomId };
    }
    
    let lastError: any;
    let attempt = 0;
    
    // 重试逻辑
    while (attempt <= maxRetries) {
        attempt++;
        try {
            logger.log(`尝试创建直接消息房间 (尝试 ${attempt}/${maxRetries + 1})`);
            
            const roomId = await matrixClient.client.createRoom({
                preset: Preset.TrustedPrivateChat,
                is_direct: true,
                invite: [userId],
                visibility: Visibility.Private,
                name: undefined, // 让服务器自动生成房间名称
                topic: '直接消息',
            });
            
            logger.log(`成功创建直接消息房间: ${roomId} 给用户 ${userId}`);
            return { success: true, roomId:roomId.room_id };
            
        } catch (error: any) {
            lastError = error;
            const errorCode = error.errcode;
            
            // 对于某些错误，直接返回而不重试
            if (errorCode === 'M_FORBIDDEN') {
                return { 
                    success: false, 
                    error: '没有权限创建房间或邀请用户',
                    details: error.message 
                };
            } else if (errorCode === 'M_NOT_FOUND') {
                return { 
                    success: false, 
                    error: '用户不存在或无法访问',
                    details: error.message 
                };
            } else if (errorCode === 'M_LIMIT_EXCEEDED') {
                // 如果达到速率限制，可以添加延时后重试
                const retryAfter = error.retry_after_ms || 1000 * Math.pow(2, attempt);
                logger.warn(`达到速率限制，${retryAfter}ms 后重试...`);
                await new Promise(resolve => setTimeout(resolve, retryAfter));
            } else if (attempt <= maxRetries) {
                // 其他错误，指数退避
                const delay = 1000 * Math.pow(2, attempt);
                logger.warn(`创建直接消息失败，${delay}ms 后重试...`, error);
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
    }
    
    // 所有重试都失败
    logger.error(`创建直接消息失败，已达到最大重试次数 (${maxRetries + 1})`, lastError);
    return { 
        success: false, 
        error: '创建直接消息失败，请稍后重试',
        details: lastError?.message 
    };
}

// 邀请用户到现有房间
async function inviteToRoom(roomId: string, userId: string): Promise<DirectMessageResult> {
    const logger = fileLogger.sub(true, 'inviteToRoom');
    
    if (!matrixClient.client) {
        return { success: false, error: 'Matrix 客户端未初始化' };
    }
    
    try {
        // 检查房间是否存在
        const room = await matrixClient.client.getRoom(roomId);
        if (!room) {
            return { success: false, error: '房间不存在' };
        }
        if (typeof room.getMember !== 'function') {
            logger.error('room 对象异常或不支持 getMember 方法', room);
            return { success: false, error: '房间对象异常或不支持成员查询' };
        }
        // 检查用户是否已经在房间中
        const member = room.getMember(userId);
        if (member?.membership === 'join') {
            return { success: true, roomId };
        }
        // 发送邀请
        await matrixClient.client.invite(roomId, userId);
        logger.log(`已邀请用户 ${userId} 加入房间 ${roomId}`);
        return { success: true, roomId };
        
    } catch (error: any) {
        logger.error('邀请用户到房间失败:', error);
        const errorCode = error.errcode;
        
        if (errorCode === 'M_FORBIDDEN') {
            return { 
                success: false, 
                error: '没有权限邀请用户',
                details: error.message 
            };
        } else if (errorCode === 'M_NOT_FOUND') {
            return { 
                success: false, 
                error: '用户或房间不存在',
                details: error.message 
            };
        } else {
            return { 
                success: false, 
                error: '邀请用户失败',
                details: error.message 
            };
        }
    }
}

export const matrixInivte = {
    createDirectMessage,
    inviteToRoom,
};